import { supabase } from '../../../integrations/supabase/client';
import { FlowData, AgentTrainingFlow, SaveFlowParams, UpdateFlowParams } from '../types/flowTypes';

const getActiveWhatsAppPhone = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data } = await supabase
    .from('whatsapp_connections')
    .select('numero_telefono')
    .eq('user_id', user.id)
    .eq('estado', 'connected')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  return data?.numero_telefono || null;
};

export const trainingFlowsApi = {
  // Guardar un nuevo flujo
  async saveFlow(params: SaveFlowParams): Promise<any> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    const numeroTelefono = await getActiveWhatsAppPhone();

    const { data, error } = await supabase
      .from('agent_training_flows')
      .insert([{
        user_id: user.id,
        agent_id: params.agentId || null,
        numero_telefono: numeroTelefono,
        nombre: params.nombre,
        descripcion: params.descripcion || null,
        flow_data: params.flowData as any,
        es_plantilla: params.esPlantilla || false
      }])
      .select()
      .single();

    if (error) throw error;
    
    // Si no tiene agent_id y el número de teléfono es válido, usar el flow_id como agent_id
    if (!params.agentId && numeroTelefono && data) {
      const { error: updateError } = await supabase
        .from('agent_training_flows')
        .update({ agent_id: data.id })
        .eq('id', data.id);
      
      if (updateError) console.warn('No se pudo actualizar agent_id:', updateError);
    }
    
    return data;
  },

  // Actualizar un flujo existente
  async updateFlow(params: UpdateFlowParams): Promise<AgentTrainingFlow> {
    const { data, error } = await supabase
      .from('agent_training_flows')
      .update({
        agent_id: params.agentId || null,
        nombre: params.nombre,
        descripcion: params.descripcion || null,
        flow_data: params.flowData as any,
        es_plantilla: params.esPlantilla || false
      })
      .eq('id', params.flowId)
      .select()
      .single();

    if (error) throw error;
    return data as AgentTrainingFlow;
  },

  // Cargar un flujo por ID
  async loadFlow(flowId: string): Promise<AgentTrainingFlow> {
    const { data, error } = await supabase
      .from('agent_training_flows')
      .select('*')
      .eq('id', flowId)
      .single();

    if (error) throw error;
    return data as AgentTrainingFlow;
  },

  // Listar flujos del usuario (opcionalmente filtrados por agente)
  async listFlows(agentId?: string): Promise<AgentTrainingFlow[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.warn('Usuario no autenticado al intentar listar flujos');
      return [];
    }

    let query = supabase
      .from('agent_training_flows')
      .select('*')
      .eq('user_id', user.id)
      .eq('es_plantilla', false)
      .order('updated_at', { ascending: false });

    if (agentId) {
      query = query.eq('agent_id', agentId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as AgentTrainingFlow[];
  },

  // Listar plantillas públicas
  async listPlantillas(): Promise<AgentTrainingFlow[]> {
    const { data, error } = await supabase
      .from('agent_training_flows')
      .select('*')
      .eq('es_plantilla', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as AgentTrainingFlow[];
  },

  // Listar workflows (flows sin numero_telefono asignado)
  async listWorkflows(): Promise<AgentTrainingFlow[]> {
    console.log('[trainingFlowsApi] 🔍 Listando workflows (sin canal)...');
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.warn('[trainingFlowsApi] ⚠️ Usuario no autenticado');
      return [];
    }

    const { data, error } = await supabase
      .from('agent_training_flows')
      .select('*')
      .eq('user_id', user.id)
      .eq('es_plantilla', false)
      .is('numero_telefono', null)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[trainingFlowsApi] ❌ Error listando workflows:', error);
      throw error;
    }
    
    console.log('[trainingFlowsApi] ✅ Workflows encontrados:', data?.length || 0);
    console.log('[trainingFlowsApi] 📋 Workflows:', data?.map(d => ({ 
      id: d.id, 
      nombre: d.nombre, 
      numero_telefono: d.numero_telefono 
    })));
    
    return (data || []) as AgentTrainingFlow[];
  },

  // Eliminar un flujo
  async deleteFlow(flowId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    const { error } = await supabase
      .from('agent_training_flows')
      .delete()
      .eq('id', flowId)
      .eq('user_id', user.id);

    if (error) throw error;
  },

  // Eliminar flow con todos sus datos relacionados (cascada)
  async deleteFlowWithCascade(flowId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    // 1. Obtener el flow para saber el agent_id
    const flow = await this.loadFlow(flowId);
    
    // 2. Eliminar mensajes de entrenamiento
    await supabase
      .from('training_messages')
      .delete()
      .eq('flow_id', flowId)
      .eq('user_id', user.id);
    
    // 3. Eliminar grupos
    await supabase
      .from('training_groups')
      .delete()
      .eq('flow_id', flowId)
      .eq('user_id', user.id);
    
    // 4. Eliminar configuración del agente
    if (flow.agent_id) {
      await supabase
        .from('agent_config')
        .delete()
        .eq('agent_id', flow.agent_id)
        .eq('user_id', user.id);
    }
    
    // 5. Finalmente eliminar el flujo
    await this.deleteFlow(flowId);
  },

  // Activar un flujo (desactiva automáticamente los demás del mismo agente)
  async activateFlow(flowId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    // 1. Desactivar todos los otros workflows del usuario
    await supabase
      .from('agent_training_flows')
      .update({ is_active: false })
      .eq('user_id', user.id)
      .neq('id', flowId);

    // 2. Activar el workflow seleccionado
    const { error } = await supabase
      .from('agent_training_flows')
      .update({ is_active: true })
      .eq('id', flowId);

    if (error) throw error;
  },

  // Desactivar un flujo
  async deactivateFlow(flowId: string): Promise<void> {
    const { error } = await supabase
      .from('agent_training_flows')
      .update({ is_active: false })
      .eq('id', flowId);

    if (error) throw error;
  },

  // Obtener el flujo activo para un agente específico
  async getActiveFlow(agentId: string): Promise<AgentTrainingFlow | null> {
    const { data, error } = await supabase
      .from('agent_training_flows')
      .select('*')
      .eq('agent_id', agentId)
      .eq('is_active', true)
      .maybeSingle();

    if (error) throw error;
    return data as AgentTrainingFlow | null;
  },

  // Exportar flujo como JSON
  exportFlow(flow: AgentTrainingFlow): void {
    const dataStr = JSON.stringify(flow.flow_data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${flow.nombre.replace(/\s+/g, '_')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  // Importar flujo desde JSON
  async importFlow(file: File): Promise<FlowData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const flowData = JSON.parse(e.target?.result as string);
          if (!flowData.nodes || !flowData.edges || !flowData.groups) {
            throw new Error('Formato de archivo inválido');
          }
          resolve(flowData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsText(file);
    });
  },

  // Crear un flujo vacío desde cero
  async createEmptyFlow(nombre: string, descripcion: string): Promise<AgentTrainingFlow> {
    console.log('[trainingFlowsApi] 🆕 Creando flow vacío:', { nombre, descripcion });
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('[trainingFlowsApi] ❌ Usuario no autenticado');
      throw new Error('Usuario no autenticado');
    }

    const emptyFlowData: FlowData = {
      nodes: [],
      edges: [],
      groups: [],
      viewport: { x: 0, y: 0, zoom: 1 }
    };

    console.log('[trainingFlowsApi] 💾 Insertando en BD...');
    
    const { data, error } = await supabase
      .from('agent_training_flows')
      .insert([{
        user_id: user.id,
        agent_id: null, // Sin agent_id hasta que se conecte a un canal
        numero_telefono: null, // Sin número hasta que se conecte
        nombre: nombre,
        descripcion: descripcion || null,
        flow_data: emptyFlowData as any,
        es_plantilla: false,
        is_active: false
      }])
      .select()
      .single();

    if (error) {
      console.error('[trainingFlowsApi] ❌ Error creando flow:', error);
      throw error;
    }

    console.log('[trainingFlowsApi] ✅ Flow creado exitosamente:', {
      id: data.id,
      nombre: data.nombre,
      numero_telefono: data.numero_telefono,
      es_plantilla: data.es_plantilla
    });

    return data as AgentTrainingFlow;
  },

  // Clonar un flujo existente
  async cloneFlow(sourceFlowId: string, newName: string, numeroTelefono: string): Promise<AgentTrainingFlow> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    // Cargar el flujo original
    const sourceFlow = await this.loadFlow(sourceFlowId);
    if (!sourceFlow) throw new Error('Flujo original no encontrado');

    // Crear nuevo flujo con los datos clonados
    const { data, error } = await supabase
      .from('agent_training_flows')
      .insert([{
        user_id: user.id,
        agent_id: sourceFlow.agent_id, // Mantener referencia al agente original
        numero_telefono: numeroTelefono,
        nombre: newName,
        descripcion: sourceFlow.descripcion,
        flow_data: sourceFlow.flow_data,
        es_plantilla: false,
        is_active: false
      }])
      .select()
      .single();

    if (error) throw error;
    return data as AgentTrainingFlow;
  },

  // Actualizar el número de teléfono de un flujo
  async updateFlowPhone(flowId: string, numeroTelefono: string): Promise<void> {
    const { error } = await supabase
      .from('agent_training_flows')
      .update({ numero_telefono: numeroTelefono })
      .eq('id', flowId);

    if (error) throw error;
  },

  // Desconectar agente (quitar número de teléfono para convertirlo de nuevo en workflow)
  async disconnectAgent(flowId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    const { error } = await supabase
      .from('agent_training_flows')
      .update({ 
        numero_telefono: null,
        is_active: false
      })
      .eq('id', flowId)
      .eq('user_id', user.id);

    if (error) throw error;
  },

  // Conectar un workflow existente a un agente (asignarle número de teléfono)
  async connectWorkflowToAgent(workflowId: string, numeroTelefono: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    // 1. Validar que el workflow existe y pertenece al usuario
    const { data: workflow, error: loadError } = await supabase
      .from('agent_training_flows')
      .select('*')
      .eq('id', workflowId)
      .eq('user_id', user.id)
      .single();

    if (loadError) throw loadError;
    if (!workflow) throw new Error('Workflow no encontrado');

    // 2. Validar que el workflow NO tiene número asignado
    if (workflow.numero_telefono) {
      throw new Error('Este workflow ya está conectado a un canal');
    }

    // 3. Validar que el número no esté en uso por otro workflow
    const { data: existingFlow, error: checkError } = await supabase
      .from('agent_training_flows')
      .select('id, nombre')
      .eq('numero_telefono', numeroTelefono)
      .neq('id', workflowId)
      .maybeSingle();

    if (checkError) throw checkError;
    if (existingFlow) {
      throw new Error(`El número ${numeroTelefono} ya está asignado al workflow "${existingFlow.nombre}"`);
    }

    // 4. Actualizar el número de teléfono y agent_id
    const { error: updateError } = await supabase
      .from('agent_training_flows')
      .update({ 
        numero_telefono: numeroTelefono,
        agent_id: workflowId // Usar su propio ID como agent_id
      })
      .eq('id', workflowId);

    if (updateError) throw updateError;
  }
};
